import React from 'react';
import { Controller, type Control, type FieldErrors } from 'react-hook-form';
import { Col, Row } from 'antd';
import { AppTag, AppTextField } from '@/components/atoms';
import type { CreateMassagerFormData } from '@/schemas/createMassagerSchema';

export type LicenseFormMode = "A2" | "R2";

interface MassagerLicenseFormProps {
  control: Control<CreateMassagerFormData>;
  errors: FieldErrors<CreateMassagerFormData>;
  /** A2: はり免許番号・きゅう免許番号. R2: 免許番号 (single field → massage_license_number). */
  mode?: LicenseFormMode;
  /** When true (e.g. BOTH layout), box is full width and has min-height to match paired R2 box. */
  fullWidthEqualHeight?: boolean;
  disabled?: boolean;
}

export const MassagerLicenseForm: React.FC<MassagerLicenseFormProps> = ({
  control,
  errors,
  mode = "A2",
  fullWidthEqualHeight = false,
  disabled = false,
}) => {
  /** R2: 免許番号 is rendered in CreateMassagerForm next to 登録日 (no box). */
  if (mode === "R2") {
    return null;
  }

  const boxClass = `rounded border-2 border-gray-300 p-3 ${fullWidthEqualHeight ? "min-h-[200px] w-full" : ""}`;

  return (
    <Row gutter={[16, 16]}>
      <Col span={fullWidthEqualHeight ? 24 : 12}>
        <div className={boxClass}>
          <span className="font-medium">免許番号</span>
          <AppTag color="magenta" className="ml-1">
            必須
          </AppTag>
          <span className="ml-2 text-sm text-red-600">※どちらか片方必須</span>
          <div className="mt-2 space-y-2">
            <div>
              <label className="mb-0.5 block text-sm">はり免許番号</label>
              <Controller
                name="acupuncture_license_number"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ""}
                    status={errors.acupuncture_license_number ? "error" : undefined}
                    className="w-full !h-[39px]"
                    disabled={disabled}
                  />
                )}
              />
              {errors.acupuncture_license_number?.message && (
                <p className="mt-1 text-sm text-red-500">{errors.acupuncture_license_number.message}</p>
              )}
            </div>
            <div>
              <label className="mb-0.5 block text-sm">きゅう免許番号</label>
              <Controller
                name="moxibustion_license_number"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ""}
                    status={errors.moxibustion_license_number ? "error" : undefined}
                    className="w-full !h-[39px]"
                    disabled={disabled}
                  />
                )}
              />
              {errors.moxibustion_license_number?.message && (
                <p className="mt-1 text-sm text-red-500">{errors.moxibustion_license_number.message}</p>
              )}
            </div>
          </div>
        </div>
      </Col>
      {!fullWidthEqualHeight && <Col span={12} />}
    </Row>
  );
};
